import { GetIssueResponseDtoStatusEnum } from '@issuemanager/api-client';
import { IssueStatusChip } from '@issuemanager/apps/client/components';
import {
  AppHeaderPrimaryButtonProps,
  useHeaderStore,
} from '@issuemanager/apps/client/contexts';
import { getIssueHeaderTitle } from '@issuemanager/apps/client/utils';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useIssueOutletContext } from '../../issue/hooks/useIssueOutletContext';

type Props = {
  onRequestReassignment: () => void;
};

export const useHeaderButtons = ({ onRequestReassignment }: Props) => {
  const { t } = useTranslation(['issue', 'common']);
  const {
    code,
    issue,
    canDiscardIssue,
    onDiscardIssue,
    userRoles: issueUserRoles,
  } = useIssueOutletContext();
  const { setHeaderProps } = useHeaderStore();

  const isIssueOwner = Boolean(issueUserRoles.data?.isOwner);
  const nonReassignableStatuses: GetIssueResponseDtoStatusEnum[] = [
    GetIssueResponseDtoStatusEnum.Closed,
    GetIssueResponseDtoStatusEnum.Discarded,
  ];
  const hasReassignableStatus = !nonReassignableStatuses.includes(
    issue.data?.status as GetIssueResponseDtoStatusEnum,
  );
  const showReassignButton = isIssueOwner && hasReassignableStatus;

  useEffect(() => {
    if (!issue.data) return;

    const discardButtonProps: AppHeaderPrimaryButtonProps = {
      children: t('issue:actions.discard'),
      variant: 'text',
      'data-testid': 'discard-button',
      onClick: onDiscardIssue,
    };

    const reassignButtonProps: AppHeaderPrimaryButtonProps = {
      children: t('issue:actions.requestReassignment'),
      variant: 'outlined',
      'data-testid': 'request-reassignment-button',
      onClick: onRequestReassignment,
    };

    setHeaderProps({
      viewTitle: {
        breadcrumbSections: [{ url: '/', title: t('common:appName') }],
        title: getIssueHeaderTitle({
          code,
          title: issue.data.title,
        }),
      },
      chip: issue.data?.status ? (
        <IssueStatusChip
          status={issue.data.status}
          isInitiated={issue.data.isInitiated}
        />
      ) : undefined,
      buttons: [
        ...(canDiscardIssue ? [discardButtonProps] : []),
        ...(showReassignButton ? [reassignButtonProps] : []),
      ],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issue.dataUpdatedAt, code, canDiscardIssue, showReassignButton]);
};
